// Hændelsesdirektøren (Bloons-budget) og tempodirektøren (skjult og mild, L4D-princippet).
//
// Ved hvert årsskifte får direktøren budgettet T(år) = T0 + k * år (x1,5 i finalen). Banens scriptede
// hændelser trækkes fra først. Resten købes fra banens menu efter pointpris, med reglerne: aldrig samme
// type tre gange i træk, højst X samtidige, og fra bane 2 mindst én tappbar trussel pr. år.
//
// De købte hændelser samles i to fronter: en forårsfront og en efterårsfront, hver med cirka halvdelen af
// købet og 4-8 s mellem slagene. Hver scriptet hændelse er sin egen front, og finalen er én front. En front
// kommer på prognosen 20 s før første slag og rammer aldrig tidligere; derfor planlægges næste år 20 s før
// årsskiftet. Tempodirektøren skubber resten af en front under pres og trækker den næste front frem, når
// der er stille, men aldrig scriptede fronter eller finalen.

import { difficulty, levelRampOf } from './difficulty';
import { DUO_AT_TICKS, DUO_WARN_TICKS, DUO_YEARS } from './duo';
import { nextInt } from './rng';
import { activeThreats, CARD_TICKS, hasTarget, IMPLEMENTED, isCard, isTappable, startThreat } from './threats';
import type { EventKind, FrontState, LevelDef, PlannedEvent, ScriptedEvent, SimEvent, State, ThreatKind, YearPlan } from './types';
import { TICK_HZ, YEAR_TICKS } from './types';

export const EVENT_COST: Record<ThreatKind, number> = {
  storm: 3,
  digger: 1,
  anchor: 2,
  protest: 1,
  spike: 2,
  heatwave: 2,
  dc_rush: 3,
  plant_trip: 3,
  icing: 3,
  dunkelflaute: 4,
  cable_half: 3,
  wind_bonanza: 2,
  transformer: 2,
  smaa: 0,
  duo: 0,
};

/**
 * Højst så mange købte hændelseskort pr. år (finalen: én mere), og højst én naboprotest. Kortene
 * er beslutninger, og en kortstorm gør dem til støj (playtest runde 2: "kortstorm fra start",
 * "naboprotest 9 gange pr. bane"). Budgettet, der bliver tilbage, går til andre trusler.
 */
export const MAX_CARDS_PER_YEAR = 2;
export const MAX_PROTESTS_PER_YEAR = 1;

/** Første købte hændelse tidligst 8 s inde i året, sidste senest 4 s før årsskiftet. */
export const YEAR_LEAD_TICKS = 80;
export const YEAR_TAIL_TICKS = 40;
/** Hvis for mange hændelser kører, ventes der 3 s. */
export const CONCURRENT_DELAY = 30;
/** Højst så mange farer ad gangen i finalen, uanset niveau (IGN-løkke 2). */
export const FINALE_CAP = 2;
/** Årets nye slags uden et mål prøver igen om så mange ticks (3 s), til året er ved at være slut. */
export const NEWS_RETRY_TICKS = 30;
export const STRESS_HIGH = 2;
export const CALM_TICKS = 200;
export const PULL_TICKS = 50;

/** Forårsfronten starter 8-28 s inde i året, efterårsfronten 33-54 s. */
export const SPRING_START: readonly [number, number] = [80, 280];
export const AUTUMN_START: readonly [number, number] = [330, 540];
/** Efterårsfronten må rykkes frem hertil for at nå at slutte i tide. */
const AUTUMN_EARLIEST = 300;
/** Mellem to slag i samme front. */
export const FRONT_GAP: readonly [number, number] = [40, 80];
/** Prognosen viser fronter, der starter inden for 20 s; en front rammer tidligst 20 s efter, den kom på. */
export const FORECAST_TICKS = 200;
/** Næste år planlægges, når der er 20 s tilbage af året. */
export const PLAN_AHEAD_TICKS = YEAR_TICKS - FORECAST_TICKS;
/** Under pres skubbes resten af fronten 5-8 s, når dens næste varsel er under 2 s væk. Højst to gange. */
export const PUSH_TICKS: readonly [number, number] = [50, 80];
const PUSH_WINDOW = 20;
const MAX_PUSHES = 2;
/** Stille kræver, at ingen front starter inden for 10 s. */
export const QUIET_FRONT_TICKS = 100;

function secToTicks(s: number | [number, number], rng: State['rng']['events']): number {
  if (typeof s === 'number') return Math.round(s * TICK_HZ);
  return Math.round(nextInt(rng, Math.round(s[0] * TICK_HZ), Math.round(s[1] * TICK_HZ)));
}

export function yearBudget10(state: State, year: number, finale: boolean, levelNumber = 1): number {
  const d = difficulty(state.difficulty);
  const t = d.eventBudgetT0 + d.eventBudgetK * year;
  const ramp = levelRampOf(d, levelNumber);
  // Dagens vagt "Blæsende dag": halvanden gang så meget vejr.
  const mod = state.mod === 'stormy' ? 1.5 : 1;
  return Math.round(t * 10 * ramp * (finale ? 1.5 : 1) * mod);
}

export function maxConcurrent(state: State): number {
  const d = difficulty(state.difficulty);
  return d.maxConcurrent + (state.coop ? d.coopConcurrent : 0);
}

/** Varsel i ticks. Et scriptet varsel vinder; hændelseskort står altid 12 s; ellers sværhedens varsel. */
export function warnTicksFor(state: State, s?: ScriptedEvent, kind?: ThreatKind): number {
  if (s?.warnS !== undefined) return Math.round(s.warnS * TICK_HZ);
  const k = s?.kind ?? kind;
  if (k !== undefined && isCard(k)) return CARD_TICKS;
  const base = Math.round(difficulty(state.difficulty).warnS * TICK_HZ);
  // Vindstillen skal kunne ses komme, så der er tid til at ringe til Norge og lade batterierne.
  if (k === 'dunkelflaute') return Math.max(DUNKEL_WARN_TICKS, base);
  // Værksnedbruddet giver tid til et svinghjul eller Lynreserven.
  if (k === 'plant_trip') return Math.max(TRIP_WARN_TICKS, base);
  return base;
}

/** Slags, direktøren højst køber én af om året (scriptede kommer oveni). */
export const ONCE_A_YEAR: readonly ThreatKind[] = ['dunkelflaute', 'cable_half', 'plant_trip'];

/** Mindste varsel for vindstille (15 s) og værksnedbrud (8 s). */
export const DUNKEL_WARN_TICKS = 150;
export const TRIP_WARN_TICKS = 80;

/** Ville `kind` give tre ens i træk efter sekvensen `seq`? */
export function wouldTriple(seq: readonly ThreatKind[], kind: ThreatKind): boolean {
  const n = seq.length;
  return n >= 2 && seq[n - 1] === kind && seq[n - 2] === kind;
}

function newPlanned(state: State, kind: ThreatKind, hitAt: number, scripted: boolean, warnTicks: number, finale: boolean, s?: ScriptedEvent): PlannedEvent {
  const ev: PlannedEvent = {
    id: `E${state.director.nextId++}`,
    kind,
    hitAt,
    scripted,
    warnTicks,
    shifted: 0,
    cost: EVENT_COST[kind],
    finale,
  };
  if (s?.strength !== undefined) ev.strength = s.strength;
  if (s?.trackY !== undefined) ev.trackY = s.trackY;
  if (s?.trackHalf !== undefined) ev.trackHalf = s.trackHalf;
  if (s?.targetLine !== undefined) ev.targetLine = s.targetLine;
  return ev;
}

function newFront(state: State, fixed: boolean, endBy: number): FrontState {
  return { id: `F${state.director.nextId++}`, seenAt: -1, pushes: 0, pulled: false, started: false, startAt: -1, fixed, endBy };
}

/** Efter slag, og ved samme tick efter købsrækkefølgen (id'ets tal, så E10 kommer efter E9). */
function byHit(a: PlannedEvent, b: PlannedEvent): number {
  return a.hitAt - b.hitAt || idNum(a.id) - idNum(b.id) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

function idNum(id: string): number {
  const n = Number(id.replace(/^\D+/, ''));
  return Number.isFinite(n) ? n : 0;
}

function sortPlanned(state: State): void {
  state.director.planned.sort(byHit);
}

/**
 * Læg en front ud: start i [lo, hi] og 4-8 s mellem slagene. Fronten rykkes frem (dog ikke før
 * `minStart`) og presses til sidst sammen, så sidste slag ligger senest ved `maxEnd`.
 */
function layFront(rng: State['rng']['events'], n: number, lo: number, hi: number, minStart: number, maxEnd: number): number[] {
  if (n <= 0) return [];
  let s = nextInt(rng, lo, hi);
  const gaps: number[] = [];
  for (let i = 1; i < n; i++) gaps.push(nextInt(rng, FRONT_GAP[0], FRONT_GAP[1]));
  const span = gaps.reduce((a, g) => a + g, 0);
  if (s + span > maxEnd) s = Math.max(minStart, maxEnd - span);
  if (s + span > maxEnd && span > 0) {
    // Ikke plads: mellemrummene skrumper først mod 4 s, og kun hvis det ikke er nok, under.
    const room = Math.max(0, maxEnd - s);
    const minSpan = FRONT_GAP[0] * gaps.length;
    for (let i = 0; i < gaps.length; i++) {
      const g = gaps[i] as number;
      gaps[i] =
        room >= minSpan && span > minSpan
          ? FRONT_GAP[0] + Math.floor(((g - FRONT_GAP[0]) * (room - minSpan)) / (span - minSpan))
          : Math.max(1, Math.floor((g * room) / span));
    }
  }
  const out = [s];
  for (const g of gaps) out.push((out[out.length - 1] as number) + g);
  return out;
}

/** De slags hændelser, et år låser op (tom, hvis banen ikke har en plan for det). */
export function newKinds(level: LevelDef, year: number): ThreatKind[] {
  return level.news?.[year] ?? [];
}

/** Nye slags, der ikke står i banens egen menu og ikke er scriptede: de kommer kun som årets nyhed. */
export function guestKinds(level: LevelDef): ThreatKind[] {
  const scripted = new Set((level.scripted ?? []).map((s) => s.kind));
  const out: ThreatKind[] = [];
  for (const ks of level.news ?? []) for (const k of ks) if (!level.eventMenu.includes(k) && !scripted.has(k) && !out.includes(k)) out.push(k);
  return out;
}

/** Det, direktøren må købe i et år: alt låst op til og med året, eller hele menuen i finalen. */
export function unlockedKinds(level: LevelDef, year: number, finale: boolean): Set<ThreatKind> {
  if (!level.news || finale) return new Set(level.eventMenu);
  const out = new Set<ThreatKind>();
  for (let y = 0; y <= year; y++) for (const k of level.news[y] ?? []) out.add(k);
  return out;
}

/**
 * Planlæg et år (eller finalen) uden at røre årets plan. `history` er de typer, der allerede er sendt
 * eller står til at blive det, så året ikke åbner med tre ens i træk.
 */
export function buildYear(state: State, level: LevelDef, year: number, finale: boolean, history: readonly ThreatKind[]): YearPlan {
  const rng = state.rng.events;
  const d = difficulty(state.difficulty);
  const start = year * YEAR_TICKS;
  const end = finale ? start + level.finaleS * TICK_HZ : start + YEAR_TICKS;
  const endBy = end - YEAR_TAIL_TICKS;
  const plan: YearPlan = { year, finale, planned: [], fronts: [], budget10: yearBudget10(state, year, finale, level.number), spent10: 0 };
  const finaleFront = finale ? newFront(state, true, endBy) : null;
  if (finaleFront) plan.fronts.push(finaleFront);

  // Orkan: ingen storme før første årsskifte, heller ikke scriptede.
  const stormFree = !finale && year < d.stormFreeYears;

  // 1. Scriptede hændelser for dette år trækkes fra først. Hver er sin egen front (øvelsesstormen ingen).
  for (const s of level.scripted) {
    const isFinale = s.finale === true;
    if (isFinale !== finale) continue;
    if (stormFree && s.kind === 'storm') continue;
    if (s.tutorial && d.stormFreeYears > 0) continue;
    const offset = secToTicks(s.at, rng);
    const hitAt = isFinale ? start + offset : offset;
    if (!isFinale && (hitAt < start || hitAt >= end)) continue;
    const ev = newPlanned(state, s.kind, hitAt, true, warnTicksFor(state, s), finale, s);
    if (s.tutorial) {
      ev.tutorial = true;
    } else if (finaleFront) {
      ev.front = finaleFront.id;
    } else {
      const f = newFront(state, true, Math.max(endBy, hitAt));
      plan.fronts.push(f);
      ev.front = f.id;
    }
    plan.planned.push(ev);
    plan.spent10 += EVENT_COST[s.kind] * 10;
  }
  // Spor "coop": rum med to mennesker får en fast fælles alarm i år 2 og 4. Sin egen faste front, gratis
  // for direktøren og uden terning, så den kommer på samme sekund på begge skærme og hver gang.
  if (state.duo && !finale && DUO_YEARS.includes(year) && year < level.years) {
    const ev = newPlanned(state, 'duo', start + DUO_AT_TICKS, true, DUO_WARN_TICKS, false);
    const f = newFront(state, true, endBy);
    plan.fronts.push(f);
    ev.front = f.id;
    plan.planned.push(ev);
  }
  plan.planned.sort(byHit);

  // 2. Køb fra menuen.
  const open = unlockedKinds(level, year, finale);
  const allowed = (k: ThreatKind): boolean => IMPLEMENTED.includes(k) && !(stormFree && k === 'storm');
  const own = level.eventMenu.filter((k) => allowed(k) && open.has(k));
  // Gæster er nye slags uden for banens egen menu (bane 1: naboerne, elbilerne og varmen). De kommer
  // én gang i deres år og én gang i finalen, så en mild bane får noget nyt uden at blive en anden bane.
  const guests = guestKinds(level).filter(allowed);
  const fresh = finale ? guests : newKinds(level, year).filter((k) => allowed(k) && (own.includes(k) || guests.includes(k)));
  const menu = [...own, ...fresh.filter((k) => !own.includes(k))];
  if (menu.length === 0) return plan;
  const seq: ThreatKind[] = [...history, ...plan.planned.map((p) => p.kind)];
  const bought: ThreatKind[] = [];
  let needTappable = level.number >= 2 && menu.some(isTappable);
  let cards = plan.planned.filter((p) => isCard(p.kind)).length;
  let protests = plan.planned.filter((p) => p.kind === 'protest').length;
  const maxCards = MAX_CARDS_PER_YEAR + (finale ? 1 : 0);
  // Kort skifter: samme slags kort højst én gang om året, og (når menuen har flere slags) aldrig det
  // samme kort to gange i træk. Playtest runde 3: tre hedebølger i træk føltes som en gentagelse.
  const cardKinds = menu.filter(isCard).length;
  const lastCard = (): ThreatKind | undefined => {
    for (let i = seq.length - 1; i >= 0; i--) if (isCard(seq[i] as ThreatKind)) return seq[i];
    return undefined;
  };
  const yearKinds = (): ThreatKind[] => [...plan.planned.map((p) => p.kind), ...bought];
  // Spor: tillid (Pocket Gamer runde 4: "Spidsbølge 3 gange", "Datacenter-kø 3 gange", "Hedebølge to
  // gange i Dagens vagt"). Kortene fra menuen trækkes uden tilbagelægning: et kort, banen har vist
  // eller har planlagt, købes ikke igen. Banens scriptede kort er dens fortælling og kommer som skrevet.
  const shownCards = new Set<ThreatKind>([...(state.director.cards ?? []), ...seq.filter(isCard)]);
  for (let guard = 0; guard < 20; guard++) {
    const left = plan.budget10 - plan.spent10;
    let options = menu.filter(
      (k) =>
        EVENT_COST[k] * 10 <= left &&
        !(guests.includes(k) && yearKinds().includes(k)) &&
        !wouldTriple(seq, k) &&
        !(isCard(k) && cards >= maxCards) &&
        !(isCard(k) && yearKinds().includes(k)) &&
        !(isCard(k) && shownCards.has(k)) &&
        !(isCard(k) && cardKinds > 1 && lastCard() === k) &&
        !(k === 'protest' && protests >= MAX_PROTESTS_PER_YEAR) &&
        // Vindstille, kabelskade og værksnedbrud er store: højst én af hver slags om året fra menuen.
        !(ONCE_A_YEAR.includes(k) && yearKinds().includes(k)),
    );
    let news = options.filter((k) => fresh.includes(k) && !yearKinds().includes(k));
    // Har året to nyheder, vælges den, der kan ramme noget i nettet, som det står nu.
    if (news.length > 1 && news.some((k) => hasTarget(state, k))) news = news.filter((k) => hasTarget(state, k));
    if (news.length > 0) options = news;
    else if (needTappable) {
      const tap = options.filter(isTappable);
      if (tap.length > 0) options = tap;
    }
    if (options.length === 0) break;
    const at = nextInt(rng, 0, options.length - 1);
    let kind = options[at] as ThreatKind;
    // IGN-løkke 2, runde 3 (mobil): "Spidsbølge og Hedebølge igen og igen". Bliver det et kort, trækkes
    // det som fra en pose: blandt kortene, banen har vist færrest gange. Andelen af kort er den samme.
    if (isCard(kind)) {
      const seen = (k: ThreatKind): number => seq.filter((x) => x === k).length;
      const cardOpts = options.filter(isCard);
      const least = Math.min(...cardOpts.map(seen));
      const bag = cardOpts.filter((k) => seen(k) === least);
      if (!bag.includes(kind)) kind = bag[at % bag.length] as ThreatKind;
    }
    if (isTappable(kind)) needTappable = false;
    if (isCard(kind)) {
      cards++;
      shownCards.add(kind);
    }
    if (kind === 'protest') protests++;
    bought.push(kind);
    seq.push(kind);
    plan.spent10 += EVENT_COST[kind] * 10;
  }

  // 3. Placér dem: finalen fordeler jævnt som én front, et år deler købet i forår og efterår.
  if (finaleFront) {
    const lo = start + YEAR_LEAD_TICKS;
    const n = bought.length;
    const seg = Math.floor(Math.max(1, endBy - lo) / Math.max(1, n));
    bought.forEach((kind, i) => {
      const warn = warnTicksFor(state, undefined, kind);
      const hitAt = lo + seg * i + nextInt(rng, Math.floor(seg * 0.2), Math.floor(seg * 0.8));
      const ev = newPlanned(state, kind, Math.max(hitAt, start + warn), false, warn, true);
      ev.front = finaleFront.id;
      plan.planned.push(ev);
    });
  } else {
    placeFronts(state, plan, bought, start, endBy);
  }
  // En gæst er en smagsprøve: den svageste udgave (elbilerne 30 %), så den lærer uden at vælte banen.
  for (const p of plan.planned) if (!p.scripted && guests.includes(p.kind)) p.strength = 1;
  plan.planned.sort(byHit);
  return plan;
}

/** Del årets køb i en forårs- og en efterårsfront med hver cirka halvdelen af prisen. */
function placeFronts(state: State, plan: YearPlan, bought: ThreatKind[], start: number, endBy: number): void {
  if (bought.length === 0) return;
  const rng = state.rng.events;
  // Forårets del er den sammenhængende begyndelse af købet, der kommer tættest på halvdelen af prisen,
  // så rækkefølgen (og "aldrig tre i træk") er den samme som ved købet.
  let springN: number;
  if (bought.length === 1) {
    springN = nextInt(rng, 0, 1);
  } else {
    const total = bought.reduce((a, k) => a + EVENT_COST[k], 0);
    let acc = 0;
    springN = 0;
    for (const k of bought) {
      const c = EVENT_COST[k];
      if (springN > 0 && Math.abs(acc + c - total / 2) > Math.abs(acc - total / 2)) break;
      acc += c;
      springN++;
    }
    springN = Math.max(1, Math.min(bought.length - 1, springN));
  }
  const spring = bought.slice(0, springN);
  const autumn = bought.slice(springN);
  const autumnAt = layFront(rng, autumn.length, start + AUTUMN_START[0], start + AUTUMN_START[1], start + AUTUMN_EARLIEST, endBy);
  const springEnd = autumnAt.length > 0 ? (autumnAt[0] as number) - FRONT_GAP[0] : endBy;
  // Varslet må aldrig starte før året (så ville det blive kortere): forårsfronten starter tidligst ét varsel inde.
  // Et senere slag ligger mindst 4 s pr. plads efter det første, så et langt varsel længere inde i fronten
  // (vindstillen, værksnedbruddet) kræver mindre forspring.
  const springWarn = Math.max(0, ...spring.map((k, i) => warnTicksFor(state, undefined, k) - (ONCE_A_YEAR.includes(k) ? i * FRONT_GAP[0] : 0)));
  const springLo = start + Math.max(SPRING_START[0], springWarn);
  const springAt = layFront(rng, spring.length, springLo, Math.max(springLo, start + SPRING_START[1]), springLo, springEnd);
  const add = (kinds: ThreatKind[], at: number[]): void => {
    if (kinds.length === 0) return;
    const f = newFront(state, false, endBy);
    plan.fronts.push(f);
    let t = -Infinity;
    kinds.forEach((kind, i) => {
      const warn = warnTicksFor(state, undefined, kind);
      t = Math.max(at[i] as number, t, start + warn);
      const ev = newPlanned(state, kind, t, false, warn, false);
      ev.front = f.id;
      plan.planned.push(ev);
    });
  };
  add(spring, springAt);
  add(autumn, autumnAt);
}

function install(state: State, plan: YearPlan): void {
  const dir = state.director;
  dir.planned = plan.planned;
  dir.fronts = plan.fronts;
  dir.budget10 = plan.budget10;
  dir.spent10 = plan.spent10;
  sortPlanned(state);
  guardFronts(state);
}

/**
 * Planlæg et år (eller finalen) med det samme. Kaldes ved banestart for år 0, og ved årsskiftet, hvis
 * året ikke nåede at blive planlagt i forvejen.
 */
export function planYear(state: State, level: LevelDef, year: number, finale: boolean): void {
  install(state, buildYear(state, level, year, finale, state.director.history));
}

/** Ved årsskiftet: tag året, der blev planlagt 20 s før, eller planlæg det nu. */
export function adoptYear(state: State, level: LevelDef, year: number, finale: boolean): void {
  const next = state.director.next;
  state.director.next = null;
  if (next && next.year === year && next.finale === finale) install(state, next);
  else planYear(state, level, year, finale);
}

/** Spillet sprang frem til årsskiftet: næste års fronter skal på prognosen igen. */
export function forgetSeen(state: State): void {
  for (const f of state.director.next?.fronts ?? []) f.seenAt = -1;
}

function frontEvents(planned: readonly PlannedEvent[], id: string): PlannedEvent[] {
  return planned.filter((p) => p.front === id);
}

/**
 * Prognosens løfte: når en front kommer på prognosen (første slag inden for 20 s), noteres det, og
 * ligger første slag tættere på end 20 s, flyttes hele fronten, så den holder.
 */
function guardList(state: State, planned: PlannedEvent[], fronts: readonly FrontState[]): void {
  const now = state.clockTick;
  let moved = false;
  for (const f of fronts) {
    if (f.started || f.seenAt >= 0) continue;
    const evs = frontEvents(planned, f.id);
    const first = evs[0];
    if (!first || first.hitAt - now > FORECAST_TICKS) continue;
    f.seenAt = now;
    const lack = now + FORECAST_TICKS - first.hitAt;
    if (lack <= 0) continue;
    // Fronten må ikke skubbes ind i den næste: så ville de flettes, og købets "aldrig tre i træk" brydes.
    let nextStart = Infinity;
    for (const e of planned) if (e.front !== undefined && e.front !== f.id && e.hitAt > first.hitAt) nextStart = Math.min(nextStart, e.hitAt);
    const cap = Math.min(f.endBy, nextStart - FRONT_GAP[0]);
    for (const e of evs) {
      const to = Math.min(e.hitAt + lack, Math.max(e.hitAt, cap, now + FORECAST_TICKS));
      e.shifted += to - e.hitAt;
      e.hitAt = to;
    }
    moved = true;
  }
  if (moved) planned.sort(byHit);
}

export function guardFronts(state: State): void {
  const dir = state.director;
  guardList(state, dir.planned, dir.fronts);
  if (dir.next) guardList(state, dir.next.planned, dir.next.fronts);
}

/** En front på prognosen. `startTick` er første slag; `kinds` er det, der endnu ikke er sendt af sted. */
export interface ForecastFront {
  id: string;
  startTick: number;
  kinds: EventKind[];
  /** Første hændelse er sendt af sted, og fronten er ikke ovre. */
  underWay: boolean;
}

function frontUnderWay(state: State, f: FrontState, remaining: number): boolean {
  return f.started && (remaining > 0 || state.threats.some((t) => t.front === f.id && t.phase !== 'done'));
}

function forecastList(state: State, planned: readonly PlannedEvent[], fronts: readonly FrontState[], out: ForecastFront[]): void {
  for (const f of fronts) {
    const evs = frontEvents(planned, f.id);
    const kinds = evs.map((e) => e.kind);
    if (frontUnderWay(state, f, evs.length)) {
      out.push({ id: f.id, startTick: f.startAt, kinds, underWay: true });
      continue;
    }
    const first = evs[0];
    if (f.started || !first) continue;
    if (first.hitAt - state.clockTick <= FORECAST_TICKS) out.push({ id: f.id, startTick: first.hitAt, kinds, underWay: false });
  }
}

/**
 * Fronter, der starter inden for 20 s eller er i gang, sorteret efter start. En front, der først er
 * kommet på, rammer aldrig før 20 s efter (bane 1's øvelsesstorm står ikke på prognosen).
 */
export function forecast(state: State): ForecastFront[] {
  const dir = state.director;
  const out: ForecastFront[] = [];
  forecastList(state, dir.planned, dir.fronts, out);
  if (dir.next) forecastList(state, dir.next.planned, dir.next.fronts, out);
  out.sort((a, b) => a.startTick - b.startTick || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  return out;
}

/** Er en front i gang lige nu? */
export function anyFrontUnderWay(state: State): boolean {
  return forecast(state).some((f) => f.underWay);
}

/**
 * Stille: intet mørkeur kører, ingen trussel er varslet eller i gang, ingen front starter inden for 10 s
 * (eller er i gang), og alle byer på kortet har mindst én ledning.
 */
export function isQuiet(state: State): boolean {
  for (const n of state.nodes) if (n.city && n.active && !n.phantom && n.city.clock > 0) return false;
  for (const t of state.threats) if (t.kind !== 'smaa' && (t.phase === 'warning' || t.phase === 'active')) return false;
  for (const f of forecast(state)) if (f.underWay || f.startTick - state.clockTick <= QUIET_FRONT_TICKS) return false;
  for (const n of state.nodes) {
    if (!n.city || !n.active || n.phantom) continue;
    if (!state.lines.some((l) => l.a === n.id || l.b === n.id)) return false;
  }
  return true;
}

/** Stress = kørende mørkeure + åbne brud. */
export function stress(state: State): number {
  let s = 0;
  for (const n of state.nodes) if (n.city && n.active && !n.phantom && n.city.clock > 0) s++;
  for (const l of state.lines) if (l.status === 'broken') s++;
  return s;
}

function shiftFront(evs: PlannedEvent[], d: number): void {
  for (const e of evs) {
    e.hitAt += d;
    e.shifted += d;
  }
}

/** Tempodirektøren: skub resten af en front under pres, træk den næste front frem, når der er stille. */
function tempo(state: State): void {
  const dir = state.director;
  const rng = state.rng.events;
  const now = state.clockTick;
  const st = stress(state);
  if (st === 0) dir.calmTicks++;
  else dir.calmTicks = 0;
  const loose = (id: string | undefined): FrontState | undefined => {
    const f = id === undefined ? undefined : dir.fronts.find((x) => x.id === id);
    return f && !f.fixed ? f : undefined;
  };

  if (st >= STRESS_HIGH) {
    const next = dir.planned.find((p) => loose(p.front) && now < p.hitAt - p.warnTicks);
    const f = loose(next?.front);
    if (next && f && f.pushes < MAX_PUSHES && next.hitAt - next.warnTicks - now <= PUSH_WINDOW) {
      const evs = frontEvents(dir.planned, f.id);
      const last = Math.max(...evs.map((e) => e.hitAt));
      const d = Math.min(nextInt(rng, PUSH_TICKS[0], PUSH_TICKS[1]), f.endBy - last);
      f.pushes++;
      if (d > 0) {
        shiftFront(evs, d);
        sortPlanned(state);
      }
    }
    return;
  }

  if (dir.calmTicks >= CALM_TICKS) {
    dir.calmTicks = 0;
    // Den næste front, der ikke er startet.
    const first = dir.planned.find((p) => {
      const f = loose(p.front);
      return f !== undefined && !f.started;
    });
    const f = loose(first?.front);
    if (!first || !f || f.pulled) return;
    const evs = frontEvents(dir.planned, f.id);
    // Aldrig tættere på end 20 s efter, fronten kom på prognosen, og hvert varsel skal kunne nå at stå fuldt.
    let earliest = (f.seenAt >= 0 ? f.seenAt : now) + FORECAST_TICKS;
    for (const e of evs) earliest = Math.max(earliest, now + e.warnTicks + 1 - (e.hitAt - first.hitAt));
    const target = Math.max(earliest, first.hitAt - PULL_TICKS);
    if (target < first.hitAt) {
      shiftFront(evs, target - first.hitAt);
      f.pulled = true;
      sortPlanned(state);
    }
  }
}

/** Ét tick for direktøren: planlæg næste år i tide, tempo, prognosens løfte og start af varsler. */
export function updateDirector(state: State, level: LevelDef, events: SimEvent[]): void {
  const dir = state.director;

  if (!state.finale) {
    if (!dir.next && state.clockTick % YEAR_TICKS >= PLAN_AHEAD_TICKS) {
      const year = state.year + 1;
      dir.next = buildYear(state, level, year, year >= level.years, [...dir.history, ...dir.planned.map((p) => p.kind)]);
    }
    tempo(state);
  }
  guardFronts(state);

  // Start varsler, når tiden er inde. IGN-løkke 2: finalen stablede fire farer oven i hinanden på de
  // svære niveauer ("en væg af rødt"). Finalen er en front, ikke en mur, så højst to ad gangen.
  const cap = state.finale ? Math.min(maxConcurrent(state), FINALE_CAP) : maxConcurrent(state);
  const dstrength = difficulty(state.difficulty).stormLines;
  for (let guard = 0; guard < 10; guard++) {
    const p = dir.planned[0];
    if (!p || state.clockTick < p.hitAt - p.warnTicks) break;
    // To hændelseskort på én gang er ét for meget: det næste venter, til det første er afgjort.
    const cardBusy = isCard(p.kind) && state.threats.some((t) => t.card !== undefined && t.card.answer === '' && t.phase === 'warning');
    if (activeThreats(state) >= cap || cardBusy) {
      p.hitAt = Math.max(p.hitAt, state.clockTick + p.warnTicks) + CONCURRENT_DELAY;
      sortPlanned(state);
      break;
    }
    // Årets nye slags venter på et mål i stedet for at forsvinde (spor "sværhed"): naboprotesten på
    // bane 4 kom før den første luftledning og blev aldrig til noget, så "Nyt i år" var et tomt løfte.
    if (!hasTarget(state, p.kind) && !state.finale && !p.scripted && newKinds(level, state.year).includes(p.kind)) {
      const later = state.clockTick + NEWS_RETRY_TICKS;
      if (later + p.warnTicks < (state.year + 1) * YEAR_TICKS - YEAR_TAIL_TICKS) {
        p.hitAt = later + p.warnTicks;
        sortPlanned(state);
        break;
      }
    }
    // Den fælles alarm venter også på en ledning at sigte efter (ellers fik holdet den aldrig).
    if (p.kind === 'duo' && !hasTarget(state, 'duo')) {
      const later = state.clockTick + NEWS_RETRY_TICKS;
      if (later + p.warnTicks < (state.year + 1) * YEAR_TICKS - YEAR_TAIL_TICKS) {
        p.hitAt = later + p.warnTicks;
        sortPlanned(state);
        break;
      }
    }
    dir.planned.shift();
    const f = p.front === undefined ? undefined : dir.fronts.find((x) => x.id === p.front);
    if (f && !f.started) {
      f.started = true;
      f.startAt = Math.max(p.hitAt, state.clockTick + p.warnTicks);
    }
    if (!hasTarget(state, p.kind)) continue;
    // Tempo og samtidighed kan bytte om på rækkefølgen; en købt hændelse må aldrig give tre ens i træk.
    if (!p.scripted && wouldTriple(dir.history, p.kind)) continue;
    const t = startThreat(state, p, dstrength, events);
    if (t && p.front !== undefined) t.front = p.front;
  }
}

/** Ved årsskifte: dropper hændelser, der ikke nåede at blive varslet. */
export function clearPlanned(state: State): void {
  state.director.planned = [];
  state.director.fronts = [];
}
